import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { Clock, Grid3x3, Hash, Type, ShieldAlert, ArrowRight, Hourglass } from "lucide-react";

const RULES = [
  { icon: Grid3x3, title: "Visual matrices", desc: "Pattern, rotation & spatial puzzles rendered as live geometry." },
  { icon: Hash, title: "Numerical series", desc: "Sequences, ratios and logical number patterns." },
  { icon: Type, title: "Verbal & logic", desc: "Analogies, deduction and comprehension items." },
  { icon: Clock, title: "25-minute limit", desc: "The clock runs once you begin. Pace yourself." },
];

function useCountdown(target) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!target) return;
    const tick = () => setLeft(Math.max(0, Math.floor((new Date(target) - new Date()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [target]);
  return left;
}

export const Instructions = () => {
  const navigate = useNavigate();
  const [elig, setElig] = useState(null);

  useEffect(() => {
    api.get("/test/eligibility").then((res) => setElig(res.data)).catch(() => setElig({ allowed: true }));
  }, []);

  const locked = elig && !elig.allowed;
  const left = useCountdown(locked ? elig.next_available_at : null);
  const hh = String(Math.floor(left / 3600)).padStart(2, "0");
  const mm = String(Math.floor((left % 3600) / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");

  // Auto-refresh eligibility the moment the cooldown elapses.
  useEffect(() => {
    if (locked && left === 0) {
      api.get("/test/eligibility").then((res) => setElig(res.data)).catch(() => {});
    }
  }, [locked, left]);

  return (
    <div className="min-h-screen bg-[#0B0F17]">
      <Header />
      <div className="max-w-4xl mx-auto px-5 py-12 sm:py-16">
        <div className="fade-up">
          <div className="uppercase tracking-[0.25em] text-xs font-semibold text-cyan-400 mb-3">Before you begin</div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            30 questions. 25 minutes.<br />One honest attempt.
          </h1>
          <p className="mt-5 text-slate-400 max-w-2xl leading-relaxed">
            This credited assessment measures five cognitive dimensions. Find a quiet space, avoid interruptions,
            and answer every question — unanswered items count as incorrect.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-10">
          {RULES.map((r, i) => (
            <div
              key={r.title}
              className="fade-up flex gap-4 p-5 rounded-2xl bg-[#131B2A] border border-white/10 hover:border-cyan-500/40 transition-colors"
              style={{ animationDelay: `${0.05 * i}s` }}
            >
              <div className="shrink-0 w-11 h-11 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                <r.icon className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <div className="font-semibold text-slate-100">{r.title}</div>
                <div className="text-sm text-slate-400 mt-1">{r.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex items-start gap-3 p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
          <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-200/80">
            One honest attempt at a time. No external help, calculators or search engines.
            Your detailed score &amp; certificate are available to purchase once you complete the test.
          </p>
        </div>

        {locked ? (
          <div data-testid="cooldown-banner" className="mt-10 flex flex-col sm:flex-row items-center gap-5 p-6 rounded-2xl bg-[#131B2A] border border-indigo-500/30">
            <div className="w-14 h-14 rounded-2xl bg-indigo-500/15 flex items-center justify-center shrink-0">
              <Hourglass className="w-6 h-6 text-indigo-400" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <div className="font-semibold text-slate-100">Your mind needs a reset</div>
              <div className="text-sm text-slate-400 mt-0.5">You can take your next assessment in</div>
            </div>
            <div data-testid="cooldown-timer" className="font-mono-data font-bold text-2xl text-indigo-300 tabular-nums">
              {hh}:{mm}:{ss}
            </div>
          </div>
        ) : null}

        {locked && (
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              data-testid="cooldown-view-results-button"
              onClick={() => navigate("/history")}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-indigo-500 text-white font-semibold hover:shadow-[0_0_30px_rgba(6,182,212,0.4)] transition-all"
            >
              View my previous results
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              data-testid="cooldown-leaderboard-button"
              onClick={() => navigate("/leaderboard")}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/10 text-slate-300 hover:bg-white/5 transition-colors"
            >
              See the leaderboard
            </button>
          </div>
        )}

        {!locked && (
          <button
            data-testid="start-test-button"
            onClick={() => navigate("/test")}
            disabled={!elig}
            className="mt-10 group inline-flex items-center gap-2.5 bg-gradient-to-r from-cyan-500 via-indigo-500 to-cyan-400 text-white font-semibold px-7 py-4 rounded-xl hover:shadow-[0_0_40px_rgba(6,182,212,0.45)] transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50"
          >
            Begin the assessment
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        )}
      </div>
    </div>
  );
};
